/**
 * 작성자 : 박승호
 * 날짜 : 2025.03.31
 * 경로 : 출역 관리 - 일 출역현황 (사내)
 */

import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useOutletContext, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { LIST_COUNT, PMS_NORMAL_SUB_CODE } from '../../_constants';
import { IUser, pageInfoState, userState } from '../../atoms';
import { IComCdList, ISelectBox, ITabObject } from 'customTypes';
import { BtnGhost } from '../../components/Button';
import { SearchOptions } from '../../assets/styles/SearchOptions';
import { FloatingButtonToTop } from '../../assets/styles/FloatingButtonToTop';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import { useSetAuth } from '../../utils/useSetAuth';
import { trimObject } from '../../utils/trimObject';
import { todayYYYYMMDD } from '../../utils/formatDate';
import { scrollToNodeTop } from '../../utils/scrollToNodeTop';
import { setComCdListState } from '../../utils/setComCdListState';
import { useDetectScrolledToBottom } from '../../utils/useDetectScrolledToBottom';
import useSetJobtype from '../../utils/useSetJobtype';
import useOnKeydownF9 from '../../utils/useOnKeydownF9';
import Tab from '../../components/Tab';
import Input from '../../components/Input';
import Portal from '../../components/Portal';
import TuiGrid from '../../components/Table/TuiGrid';
import SelectBox from '../../components/SelectBox';
import TuiGridWrapper from '../../components/Table/TuiGridWrapper';
import InfoAttendModal from '../../components/Modal/InfoAttendModal';
import InfoWorkerModal from '../../components/Modal/InfoWorkerModal';
import SearchSelectBoxSm from '../../components/SearchSelectBoxSm';
import EnrollmentAttendModal from '../../components/Modal/EnrollmentAttendModal';
import RangePicker, { onChangeRangeInput } from '../../components/RangePicker';
import { logPost } from '../../services/log';
import { useSetSjcd } from '../../utils/useSetSjcd';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import i18n from '../../translation/i18n';
import useSetListCount from '../../utils/useSetListCount';
import { apiGet } from '../../services/_common';
import Toggle from '../../components/Toggle';
import { isMobile } from '../../utils/checkMobile';
import ShortcutButton from '../../components/button/ShortcutButton';
import useTuiGridSize from '../../hooks/useTuiGridSize';
import { showExcelButton } from '../../utils/deviceAuth';
import SearchFilterModal from '../../components/Modal/SearchFilterModal';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem 0;

  > :nth-child(1) {
    align-self: flex-start;
  }
`;

const Cattend1 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const INIT_F_NUM = { type: 'fNum', fNum: 'A', cdName: t('전체') };
  const INIT_F_LOCATION = { type: 'fLocation', fLocation: '', cdName: t('전체') };
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const { siteJoinInfoList } = useSetSjcd();
  const location = useLocation();
  const size = useOutletContext<any>();
  const tabInfo = useRecoilValue(pageInfoState);
  const [currentTabMrCd, setCurrentTabMrCd] = useState(tabInfo.defaultMrCd);
  const toDay = todayYYYYMMDD();
  const [userInfo, setUserInfo] = useRecoilState<IUser>(userState);
  const { userMenuList, eExcelYn, eListYn, ePrintYn } = userInfo;
  const [tableState, setTableState] = useState<any[]>([]);
  const [orgTableState, setOrgTableState] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([
    {
      startDate: location.state?.startDate ? new Date(location.state?.startDate) : new Date(),
      endDate: location.state?.endDate ? new Date(location.state?.endDate) : new Date(),
      key: 'selection',
    },
  ]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [jikJongList, setJikJongList] = useState<any[]>([]);
  const [fNameList, setFnameList] = useState<any[]>([]);
  const [searchOption, setSearchOption] = useState({
    name: location.state?.name || '',
    start: location.state?.startDate || toDay,
    end: location.state?.endDate || toDay,
  });
  const [fNum, setFNum] = useState(INIT_F_NUM);
  const [partCd, setPartCd] = useState({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });
  const [positionCd, setPositionCd] = useState({ type: 'POSITION_CD', POSITION_CD: 'A', cdName: t('전체') });
  const [fLocation, setFLocation] = useState({ type: 'fLocation', fLocation: '', cdName: '' });
  const [fLocationList, setFLocationList] = useState<any[]>([]);
  const [wPrejobtype, setWPrejobtype] = useState({ type: 'wPrejobtype', wPrejobtype: '', cdName: '' });
  const [wJobtype, setWJobtype] = useState({ type: 'wJobtype', wJobtype: '', cdName: '' });
  const { prejobtypeList, jobtypeList } = useSetJobtype(wPrejobtype.wPrejobtype);
  const [excelBtn, setExcelBtn] = useState(false);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: { wCd: '' }, index: 0 });
  const [newTabList, setNewTabList] = useState<ITabObject[]>();
  const [rowKey, setRowKey] = useState('');
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAbsent, setShowAbsent] = useState(false);
  const [initiate, setinitiate] = useState('');
  const [partList, setPartList] = useState<ISelectBox[]>([]); // 부서명 메뉴 리스트
  const [positionList, setPositionList] = useState<ISelectBox[]>([]); // 직급명 메뉴 리스트
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const showExcelBtn = showExcelButton(auth.excelAuth);
  const [searchParams] = useSearchParams();
  const tabCd = searchParams.get('tabCd');

  // URL의 tabCd 파라미터 변경 감지하여 탭 업데이트
  useEffect(() => {
    if (tabCd && tabCd !== currentTabMrCd) {
      setCurrentTabMrCd(tabCd);
    }
  }, [tabCd]);

  useEffect(() => {
    const arr: any[] = [];
    userMenuList.map((v: any) => {
      const result = tabInfo?.tabList?.find((el: ITabObject) => el.mtCd === v.mtCd && el.mdCd === v.mdCd && el.mrCd === v.mrCd);
      if (result) {
        arr.push(result);
        setNewTabList(arr);
      }
    });
  }, [tabInfo.tabList]);

  useEffect(() => {
    setLoading(true);
    getAttendDayAPI().then((res: any) => {
      if (res.status === 200) {
        getNormalCd();
        getFLocation();
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일 출역현황',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    const mobile = isMobile();
    const allColumns = [
      { header: t('근로자명'), name: 'wName', align: 'center', sortable: true, width: 120, renderer: { classNames: ['text_secondary'] } },
      { header: t('부서명'), name: 'partName', align: 'center', sortable: true, filter: 'select', width: 200, renderer: { classNames: ['text_secondary'] } },
      { header: t('직급명'), name: 'positionName', align: 'center', sortable: true, filter: 'select', width: 200, renderer: { classNames: ['text_secondary'] } },
      { header: t('출역일자'), name: 'aDate', align: 'center', sortable: true, filter: 'select', minWidth: 200, renderer: { classNames: ['text_secondary'] } },
      { header: t('출근시간'), name: 'aTimeMin', align: 'center', sortable: true, minWidth: 200, renderer: { classNames: ['text_secondary'] } },
      { header: t('퇴근시간'), name: 'aTimeMax', align: 'center', sortable: true, minWidth: 200, renderer: { classNames: ['text_secondary'] } },
      { header: t('체온'), name: 'tempMax', align: 'center', sortable: true, minWidth: 100 },
      { header: t('알코올 농도'), name: 'wAlcohol', align: 'center', sortable: true, minWidth: 100 },
    ];
    if (mobile) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const containerPadding = 0.75 * 2 * rootFontSize;
      const colWidth = Math.floor((document.body.clientWidth - containerPadding) / 4);
      const mobileColumns = [
        { header: t('근로자명'), name: 'wName', align: 'center', sortable: true, width: colWidth, renderer: { classNames: ['text_secondary'] } },
        { header: t('출근시간'), name: 'aTimeMin', align: 'center', sortable: true, width: colWidth, renderer: { classNames: ['text_secondary'] } },
        { header: t('퇴근시간'), name: 'aTimeMax', align: 'center', sortable: true, width: colWidth, renderer: { classNames: ['text_secondary'] } },
        { header: t('체온'), name: 'tempMax', align: 'center', sortable: true, width: colWidth },
      ];
      setColumns(mobileColumns);
    } else {
      setColumns(allColumns);
    }
  }, [i18n.language]);

  useEffect(() => {
    setWJobtype({ type: 'wJobtype', wJobtype: '', cdName: '' });
    if (wPrejobtype.wPrejobtype === '') setJikJongList([]);
  }, [wPrejobtype.wPrejobtype]);

  useEffect(() => {
    setJikJongList(jobtypeList);
  }, [jobtypeList]);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  // 일 출역현황 조회 API
  // skipSetTable=true이면 setTableState를 호출하지 않고 setOrgTableState만 갱신 (미출석 모드에서 깜빡임 방지)
  const getAttendDayAPI = async (skipSetTable = false) => {
    const newSearchOption = {
      ...searchOption,
      partCd: partCd.PART_CD === 'A' ? '' : partCd.PART_CD,
      positionCd: positionCd.POSITION_CD === 'A' ? '' : positionCd.POSITION_CD,
      fNum: fNum.fNum === 'A' ? '' : fNum.fNum,
      // sjCd: sjCd.sjCd,
      aDate1: searchOption.start.replaceAll('-', ''),
      aDate2: searchOption.end.replaceAll('-', ''),
      // wPrejobtype: wPrejobtype.wPrejobtype,
      // wJobtype: wJobtype.wJobtype,
      fLocation: fLocation.fLocation,
    };
    const { start, end, ...finalSearchObj } = newSearchOption; // searchOption에서 start, end값 삭제
    const trimData = trimObject(finalSearchObj);
    const req = { ...trimData, cCd: userInfo.hCd };

    const res = await apiGet({ path: '/attend/day/company', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      setLoading(false);
      setOrgTableState(data.attendList);
      if (!skipSetTable) {
        setTableState(data.attendList);
      }
    } else {
      setLoading(false);
      // toast.error(t(ERROR));
    }
    return res;
  };

  // 장비 리스트 조회 API
  const getNormalCd = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd: 'I' };
    const res = await apiGet({ path: '/code/normal/site', req });

    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_NUM);
      data.normalList.map((v: any) => {
        newList.push({ type: 'fNum', fNum: v.subCd, cdName: v.cdName });
      });
      setFnameList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 출입위치 리스트 조회 API
  const getFLocation = async () => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd: 'J' };
    const res = await apiGet({ path: '/code/normal/site', req });

    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const newList = [];
      newList.push(INIT_F_LOCATION);
      data.normalList.map((v: any) => {
        newList.push({ type: 'fLocation', fLocation: v.subCd, cdName: v.cdName });
      });
      setFLocationList(newList);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 검색 버튼 클릭
  const onClickSearch = () => {
    setRowKey('');
    setShowAbsent(false);
    if (!openModal.status) {
      if (!searchOption.end && !searchOption.start) {
        toast.warning(t('출역일자를 입력하세요'));
      } else if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('출역일자를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        setLoading(true);
        setVisibleRangePicker(false);
        // 모바일: 검색 시 포커스 blur로 가상 키보드 내림
        if (isMobile()) (document.activeElement as HTMLElement | null)?.blur?.();
        // replace: true - push 시 검색마다 히스토리가 쌓여 키보드 내리기(=안드로이드 back)가 뒤로가기로 동작하는 문제 방지
        navigate({ pathname: location.pathname, search: '' }, { replace: true });
        getAttendDayAPI();
      }
    }
  };

  // 미출석 토글
  const handleAbsentToggle = async () => {
    if (showAbsent) {
      setShowAbsent(false);
      setTableState(orgTableState);
      return;
    }
    setLoading(true);
    const req = { cCd: userInfo.hCd, partCd: partCd.PART_CD !== 'A' ? partCd.PART_CD : '', name: searchOption.name };
    const res = await apiGet({ path: '/worker/company', req });
    const { statusCode, data } = res.data;
    if (statusCode === 200) {
      const attendedSet = new Set(orgTableState.map((r: any) => r.wCd));
      const absentList = data.workerList
        .filter((w: any) => !attendedSet.has(w.wCd))
        .map((w: any) => ({
          wCd: w.wCd,
          wName: w.wName,
          partName: partList.find((p: any) => p.PART_CD === w.partCd)?.cdName || '',
          positionName: '',
          aDate: '',
          aTimeMin: '',
          aTimeMax: '',
          tempMax: '',
          wAlcohol: '',
        }));
      setTableState(absentList);
      setShowAbsent(true);
    }
    setLoading(false);
  };

  const initiateSearchOptions = () => {
    setPartCd({ type: 'PART_CD', PART_CD: 'A', cdName: t('전체') });
    setPositionCd({ type: 'POSITION_CD', POSITION_CD: 'A', cdName: t('전체') });
    setFNum(INIT_F_NUM);
    setFLocation(INIT_F_LOCATION);
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    if (location.state?.sjName) {
      location.state.sjName = '';
    }
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setinitiate(`${Math.random()}`);
    initiateSearchOptions();
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  };

  // 신규 등록 버튼 클릭
  const onClickNew = () => {
    setOpenModal({ status: true, type: 'enrollmentAttend' });
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    if (tableState.length <= 0) return;
    setExcelBtn(true);
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '일 출역현황(사내)',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  // 인쇄 버튼 클릭
  const onClickPrint = () => {
    handlePrint();
  };

  // 부서명, 직급명 조회
  const setPmsNormalSubList = async (subCd: string, setState: Dispatch<SetStateAction<ISelectBox[]>>) => {
    const req = { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { status } = res;
    const cdFind: string = PMS_NORMAL_SUB_CODE.find((el) => el.code.includes(subCd))?.type || '';
    if (status === 200) {
      const defaultObject = { type: cdFind, [cdFind]: 'A', cdName: t('전체') };
      const newArr = res.data.data.pmsNormalTitleList?.map((el: ISelectBox) => ({ type: cdFind, [cdFind]: el.subCd, cdName: el.cdName }));
      setState([defaultObject, ...newArr]);
    }
  };

  useEffect(() => {
    setPmsNormalSubList('L', setPartList);
    setPmsNormalSubList('M', setPositionList);
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  // 근로자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
    setOpenModal({ status: true, type: 'infoWorker', code: rowData.wCd, aDate: rowData.aDate, index: filteredIndex });
  };

  // 개인별 월 출역현황 메뉴 권한이 있을 때만 근로자 모달에 이동 버튼 노출
  const hasMonthlyAttendMenu = userMenuList?.some((el: any) => el.mtCd === 'C003' && el.mdCd === '005');

  // 근로자 모달 > 월 출역현황: 해당 근로자·출역월로 개인별 월 출역현황 바로 조회
  const onClickMonthlyAttend = ({ wCd, wName, aDate }: { wCd: string; wName: string; aDate: string }) => {
    const ymd = (aDate || searchOption.start).replace(/\D/g, ''); // 미출역 행은 aDate가 없어 조회 시작일 사용
    setOpenModal({ status: false, type: '' });
    // 모바일(W<=1024)은 SearchFilterModal(useBackHistory)이 모달 열림 시 더미 히스토리를 push해 두므로 replace로 대체 (뒤로가기 1회로 일 출역현황 복귀)
    navigate('/c_attend5', { state: { wCd, wName, year: ymd.substring(0, 4), month: ymd.substring(4, 6) }, replace: size.innerSize.W <= 1024 });
  };

  const renderNewBtn = () => {
    if (auth.createAuth) {
      return (
        <BtnGhost onClick={onClickNew}>
          <span className='material-symbols-rounded'>add</span> {t('추가')}
        </BtnGhost>
      );
    }
    return null;
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
      // return <BtnLightBlue onClick={onClickExcelSave}>엑셀 저장</BtnLightBlue>;
    }
    return null;
  };

  const renderPrintBtn = () => {
    if (auth.printAuth) {
      return (
        <BtnGhost onClick={onClickPrint}>
          <span className='material-symbols-rounded'>print</span>
          {t('인쇄')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickTab = (tab: string) => {
    setCurrentTabMrCd(tab);
  };

  // 근로자명 입력창에서 엔터입력시 검색
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (nameRef.current) onClickSearch();
    }
  };

  const onClickFilter = () => {
    setOpenModal({ ...openModal, status: true, type: 'filter' });
  };

  const handlFilterSearch = async () => {
    setOpenModal({ ...openModal, status: false, type: '' });
    setRowKey('');
    if (!searchOption.end && !searchOption.start) {
      toast.warning(t('출역일자를 입력하세요'));
    } else if (searchOption.end && searchOption.start > searchOption.end) {
      toast.warning(t('출역일자를 다시 입력하세요'));
      setSearchOption((prev: any) => ({ ...prev, end: '' }));
    } else {
      setLoading(true);
      setVisibleRangePicker(false);
      // 모바일: 검색 시 포커스 blur로 가상 키보드 내림
      if (isMobile()) (document.activeElement as HTMLElement | null)?.blur?.();
      // replace: true - 히스토리 누적 방지 (키보드 내리기=안드로이드 back 이 뒤로가기로 동작하던 문제)
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      // showAbsent=true이면 출역 목록이 잠깐 렌더링되는 깜빡임을 막기 위해 setTableState 건너뜀
      const res = await getAttendDayAPI(showAbsent);
      if (showAbsent && res?.data?.statusCode === 200) {
        const newAttendList = res.data.data.attendList;
        setLoading(true);
        const req = { cCd: userInfo.hCd, partCd: partCd.PART_CD !== 'A' ? partCd.PART_CD : '', name: searchOption.name };
        try {
          const absentRes = await apiGet({ path: '/worker/company', req });
          const { statusCode: absentStatus, data: absentData } = absentRes.data;
          if (absentStatus === 200) {
            const attendedSet = new Set(newAttendList.map((r: any) => r.wCd));
            const absentList = absentData.workerList
              .filter((w: any) => !attendedSet.has(w.wCd))
              .map((w: any) => ({
                wCd: w.wCd,
                wName: w.wName,
                partName: partList.find((p: any) => p.PART_CD === w.partCd)?.cdName || '',
                positionName: '',
                aDate: '',
                aTimeMin: '',
                aTimeMax: '',
                tempMax: '',
                wAlcohol: '',
              }));
            setTableState(absentList);
          }
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Tab tabList={newTabList || tabInfo.tabList} currentTabMrCd={currentTabMrCd} setCurrentTabMrCd={setCurrentTabMrCd} onClickTab={onClickTab} size={size} />
        <Root loading={loading}>
          <SearchOptions>
            {isBottom && (
              <FloatingButtonToTop>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                  <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                </button>
              </FloatingButtonToTop>
            )}
            {size.innerSize.W > 1024 ? (
              <>
                <div className='inputsWrapper'>
                  <div className='inputForm-row withLabel'>
                    <label htmlFor='partCd'>{t('부서명')}</label>
                    <div className='inputForm-col'>
                      <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
                    </div>
                  </div>
                  <div className='inputForm-row withLabel'>
                    <label htmlFor='positionCd'>{t('직급명')}</label>
                    <div className='inputForm-col'>
                      <SelectBox
                        options={positionList}
                        defaultOption={t('전체')}
                        state={positionCd}
                        setState={setPositionCd}
                        stateKey='POSITION_CD'
                        initiateKey={positionCd.POSITION_CD}
                        filterbar='filter-1-left'
                      />
                    </div>
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='wForeignyn'>{t('장비명')}</label>
                    <SelectBox
                      options={fNameList} //
                      defaultOption={t('전체')}
                      state={fNum}
                      setState={setFNum}
                      stateKey='fNum'
                      initiateKey={fNum.fNum}
                      filterbar='filter-1-left'
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-col withLabelComCf'>
                    <label htmlFor='fLocation'>{t('출입위치')}</label>
                    <SelectBox
                      options={fLocationList} //
                      defaultOption={t('전체')}
                      state={fLocation}
                      setState={setFLocation}
                      stateKey='fLocation'
                      initiateKey={fLocation.fLocation}
                      filterbar='filter-1-left'
                      optionHeight='height-md'
                    />
                  </div>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('출역일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper'>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('근로자명')}
                        label=''
                        type='text'
                        id='name'
                        name='name'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={nameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        className='textInput'
                      />
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount[LIST_COUNT]}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex-basic iconBtnGroup'>
                      <Toggle onChange={handleAbsentToggle} status={showAbsent} text={t('미출석 보기')} />
                      {/* {renderNewBtn()} */}
                      {renderExcelBtn()}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <input
                        type='search'
                        value={searchOption.name}
                        onChange={(e) => setSearchOption((prev: any) => ({ ...prev, name: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handlFilterSearch();
                        }}
                        placeholder={t('근로자명')}
                        style={{ width: '100%', height: '2.5rem', padding: '0 0.75rem', background: 'var(--tonal)', borderRadius: '0.25rem', border: 'none', fontSize: '0.875rem' }}
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                </div>
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
              usePagenation
              onClickRow={onClickRow}
              scrollX={!isMobile()}
              height={tuiHeight}
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              rowKey={rowKey}
              frozenCount={1}
              setRowKey={setRowKey}
              idxKey={isMobile() ? 'not use' : undefined}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'infoAttend' && openModal.code && (
          <InfoAttendModal setOpenModal={setOpenModal} auth={auth} code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code.wCd, bSeq: openModal.code.bSeq, writer: openModal.code.writer }} />
        )}
        {openModal.status && openModal.type === 'infoWorker' && openModal.code && (
          <InfoWorkerModal
            getReportListAPI={getAttendDayAPI}
            reportArray={filterTableState}
            setReportArray={setFilterTableState}
            innerTabIndex={1}
            setOpenModal={setOpenModal}
            auth={auth}
            code={{ hCd: userInfo.hCd, sCd: userInfo.sCd, wCd: openModal.code, aDate: openModal.aDate }}
            index={openModal.index}
            setRowKey={setRowKey}
            listCount={Number(listCount[LIST_COUNT])}
            hiddenTabs={[2]}
            dGubun='c'
            onClickMonthlyAttend={hasMonthlyAttendMenu ? onClickMonthlyAttend : undefined}
          />
        )}
        {openModal.status && openModal.type === 'enrollmentAttend' && (
          <EnrollmentAttendModal getReportListAPI={getAttendDayAPI} setOpenModal={setOpenModal} auth={auth} code={{ hCd: userInfo.hCd, sCd: userInfo.sCd }} />
        )}
      </Portal>
      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handlFilterSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label>{t('출역일자')}</label>
                  <div className='inputForm-col'>
                    <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                      <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                      <span className='inputDash'> ~ </span>
                      <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                    </div>
                    {visibleRangePicker && (
                      <div className='rangePickerWrapper'>
                        <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                      </div>
                    )}
                  </div>
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='partCd'>{t('부서명')}</label>
                  <div className='inputForm-col'>
                    <SelectBox options={partList} defaultOption={t('전체')} state={partCd} setState={setPartCd} stateKey='PART_CD' initiateKey={partCd.PART_CD} filterbar='filter-1-left' />
                  </div>
                </div>
                <div className='filter-option-row'>
                  <label htmlFor='positionCd'>{t('직급명')}</label>
                  <div className='inputForm-col'>
                    <SelectBox
                      options={positionList}
                      defaultOption={t('전체')}
                      state={positionCd}
                      setState={setPositionCd}
                      stateKey='POSITION_CD'
                      initiateKey={positionCd.POSITION_CD}
                      filterbar='filter-1-left'
                    />
                  </div>
                </div>
                <div className='filter-option-row'>
                  <label>{t('장비명')}</label>
                  <SelectBox options={fNameList} defaultOption={t('전체')} state={fNum} setState={setFNum} stateKey='fNum' initiateKey={fNum.fNum} filterbar='filter-1-left' optionHeight='height-md' />
                </div>
                <div className='filter-option-row'>
                  <label>{t('출입위치')}</label>
                  <SelectBox
                    options={fLocationList}
                    defaultOption={t('전체')}
                    state={fLocation}
                    setState={setFLocation}
                    stateKey='fLocation'
                    initiateKey={fLocation.fLocation}
                    filterbar='filter-1-left'
                    optionHeight='height-md'
                  />
                </div>
                <div className='filter-option-row'>
                  <label>{t('미출역 보기')}</label>
                  <div className='inputForm-col'>
                    <Toggle onChange={handleAbsentToggle} status={showAbsent} text='' noBg />
                  </div>
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Cattend1;
